/**
 * Карась.
 *
 * The first thing in this world that lives in the water rather than on it.
 * Everything else falls: the cossacks, the orcs, the pigs and the тури all
 * have gravity pulling at them and ground under them. A fish has neither -
 * it hangs where it is, and the only rule it obeys absolutely is that it
 * stays wet.
 *
 * That inversion is the whole of this file. Where a pig refuses a step
 * because the cell ahead *is* water, a карась refuses one because the cell
 * ahead is *not* - which is why it is not the beast code with a flag, and
 * why the two cannot drift into agreeing about the same mistake.
 *
 * Pure: positions, a clock and two block tests. "Can a fish leave the lake"
 * is a question the test suite answers.
 */

import type { SolidTest } from "../physics/voxelCollision.js";
import { Vitals } from "./vitals.js";
import { PlainItemId } from "../block/items.js";

export const KARAS_MAX_HP = 4;
/** Blocks a second. Unhurried - a карась is not a pike. */
export const KARAS_SPEED = 1.1;
/** And when something has frightened it. */
export const KARAS_DART = 3.4;
/** How long a startled fish keeps darting. */
export const DARTED_SECONDS = 4;
/** How long one leg of a swim lasts before it turns. */
const LEG_SECONDS = 3;
/** Share of legs spent hanging still in the water. */
const IDLE_SHARE = 0.3;
/** How far it will rise or sink on one leg, as a share of its speed. */
const CLIMB = 0.35;

/** What one is worth on the bank. */
export const KARAS_DROP = PlainItemId.RawKaras;

export interface Fish {
  readonly id: number;
  x: number;
  y: number;
  z: number;
  /** Heading, for drawing it. Yaw 0 faces -Z, as everything else does. */
  yaw: number;
  /** Nose-up or nose-down, in radians. A fish is the only thing here that has one. */
  pitch: number;
  vx: number;
  vy: number;
  vz: number;
  readonly vitals: Vitals;
  darted: number;
  threatX: number;
  threatZ: number;
  leg: number;
  legYaw: number;
  legRise: number;
  legMoving: boolean;
}

let nextId = 1;

export function makeFish(at: { x: number; y: number; z: number }): Fish {
  return {
    id: nextId++,
    x: at.x, y: at.y, z: at.z,
    yaw: 0, pitch: 0,
    vx: 0, vy: 0, vz: 0,
    vitals: new Vitals(KARAS_MAX_HP),
    darted: 0,
    threatX: 0,
    threatZ: 0,
    leg: 0,
    legYaw: 0,
    legRise: 0,
    legMoving: false,
  };
}


/**
 * One the server owns, drawn here.
 *
 * Built rather than made-and-renumbered, for the reason `mirrorOrc` is: the
 * makers take the next local id as they go, so mirroring a herd would push
 * this world's own numbering along for bodies it never made.
 */
export function mirrorFish(
  id: number, at: { x: number; y: number; z: number },
): Fish {
  return {
    id,
    x: at.x, y: at.y, z: at.z,
    yaw: 0, pitch: 0,
    vx: 0, vy: 0, vz: 0,
    vitals: new Vitals(KARAS_MAX_HP),
    darted: 0, threatX: 0, threatZ: 0,
    leg: 0, legYaw: 0, legRise: 0, legMoving: false,
  };
}

/** Something came at it. It darts away for a few seconds. */
export function startle(fish: Fish, fromX: number, fromZ: number): void {
  fish.darted = DARTED_SECONDS;
  fish.threatX = fromX;
  fish.threatZ = fromZ;
}

/**
 * Advance every fish.
 *
 * @returns the ones that went belly-up, so the caller can leave the catch
 * floating where they were - the same shape the livestock uses.
 */
export function stepFish(
  fish: Fish[],
  dt: number,
  isFluid: SolidTest,
  random: () => number = Math.random,
): Fish[] {
  const fallen: Fish[] = [];

  for (let i = fish.length - 1; i >= 0; i--) {
    const f = fish[i]!;
    f.vitals.step(dt);
    if (f.vitals.dead) {
      fish.splice(i, 1);
      fallen.push(f);
      continue;
    }
    if (f.darted > 0) f.darted = Math.max(0, f.darted - dt);

    let wantX = 0;
    let wantY = 0;
    let wantZ = 0;
    let speed = KARAS_SPEED;

    if (f.darted > 0) {
      const dx = f.x - f.threatX;
      const dz = f.z - f.threatZ;
      const flat = Math.hypot(dx, dz);
      if (flat > 0.001) {
        wantX = dx / flat;
        wantZ = dz / flat;
        speed = KARAS_DART;
      }
      // Down, as well as away: a frightened карась goes deep.
      wantY = -CLIMB;
    } else {
      f.leg -= dt;
      if (f.leg <= 0) {
        f.leg = LEG_SECONDS * (0.5 + random());
        f.legMoving = random() > IDLE_SHARE;
        f.legYaw = random() * Math.PI * 2;
        f.legRise = (random() - 0.5) * 2 * CLIMB;
      }
      if (f.legMoving) {
        wantX = -Math.sin(f.legYaw);
        wantZ = -Math.cos(f.legYaw);
        wantY = f.legRise;
      }
    }

    if (wantX !== 0 || wantZ !== 0 || wantY !== 0) {
      // Test each axis on its own, so a fish meeting the surface keeps
      // swimming along it instead of stopping dead against the sky. Combined,
      // one blocked direction would cancel the other two.
      const wet = (dx: number, dy: number, dz: number): boolean => isFluid(
        Math.floor(f.x + dx), Math.floor(f.y + dy), Math.floor(f.z + dz),
      );
      const reach = 0.5;
      if (wantX !== 0 && !wet(Math.sign(wantX) * reach, 0, 0)) wantX = 0;
      if (wantZ !== 0 && !wet(0, 0, Math.sign(wantZ) * reach)) wantZ = 0;
      if (wantY !== 0 && !wet(0, Math.sign(wantY) * reach, 0)) wantY = 0;
      // Everything refused: it is in a pocket of water one cell across, or
      // somebody has drained the pond around it. Turn, do not thrash.
      if (wantX === 0 && wantZ === 0 && wantY === 0) f.leg = 0;
    }

    f.vx = wantX * speed;
    f.vy = wantY * speed;
    f.vz = wantZ * speed;

    // No gravity and no collision solver: a fish that only ever moves into
    // water it has already tested cannot end up inside a rock.
    f.x += f.vx * dt;
    f.y += f.vy * dt;
    f.z += f.vz * dt;

    const flat = Math.hypot(f.vx, f.vz);
    if (flat > 0.01) {
      f.yaw = Math.atan2(-f.vx, -f.vz);
      f.pitch = Math.atan2(f.vy, flat);
    } else {
      f.pitch = 0;
    }
  }

  return fallen;
}
